import { useMemo } from 'react';
import { Group } from './types';

interface UseCustomGridFooterParams {
  rowCount: number;
  isGroupingEnabled?: boolean;
  groups?: Group[];
  text?: string;
}

/**
 * Custom hook to format the footer text for DataGrid with grouping support.
 *
 * @param rowCount - Total number of data rows (excluding group headers)
 * @param isGroupingEnabled - Whether grouping is currently enabled
 * @param groups - Array of user-defined groups
 *
 * @returns Formatted footer text:
 *   - When grouping enabled: "X groups, Y rows" (X = user groups + 1 for "Ungrouped")
 *   - When grouping disabled: "Y rows"
 *
 * Note: The group count includes +1 for the "Ungrouped" group, which is automatically
 * created by transformDataForGrouping() when there are items not assigned to any group.
 */
export const useCustomGridFooter = ({
  rowCount,
  isGroupingEnabled,
  groups,
  text,
}: UseCustomGridFooterParams): string => {
  const footerText = useMemo(() => {
    if (text) {
      return text;
    }

    if (isGroupingEnabled && groups && groups.length > 0) {
      const groupCount = groups.length + 1; // +1 for the "Ungrouped" group
      return `${groupCount} groups, ${rowCount} rows`;
    }
    return `${rowCount} rows`;
  }, [rowCount, isGroupingEnabled, groups, text]);

  return footerText;
};
